import isEmpty from 'lodash/isEmpty'
import type {
  PivotField,
  PivotLayout,
  PivotLayoutField,
  PivotLayoutFilter,
  PivotLayoutMeasure,
  PivotLayoutValueFilter,
} from '@ztechapps/common.contract'
import type { PivotData, PivotMember, PivotNode, PivotResult } from './types'
import type { PivotFormula } from '@ztechapps/common.utils'
import {
  PivotAggregationEnum,
  PivotValueFilterDirectionEnum,
  PivotValueFilterModeEnum,
  PivotValueFilterScopeEnum,
} from '@ztechapps/common.contract'
import {
  compilePivotFormula,
  findPivotValueFilterProblem,
  isInPivotDateRange,
  isPivotCalculatedMeasure,
} from '@ztechapps/common.utils'
import { createPivotMemberMatcher } from './createPivotMemberMatcher'
import { resolvePivotMeasureFormat } from './pivotLayout.utils'
import { sortPivotMembers, toPivotMember, toPivotMemberKey } from './pivotMember.utils'
import { PIVOT_ROOT_KEY } from './types'

type PivotCellAccumulator = {
  rows: number
  sum: number
  count: number
  distinct?: Set<string>
  numberCount: number
  max: number | undefined
  min: number | undefined
}

/** Where one accumulator slot reads its value in a source row, or how an individual formula computes it. */
type PivotSlot = { fieldIndex?: number; aggregation: PivotAggregationEnum; formula?: PivotFormula }

/** A Top / Bottom N ready to rank: the axis it sits on, its position there and the slot of its measure. */
type PivotRankedFilter = {
  valueFilter: PivotLayoutValueFilter
  items: PivotLayoutField[]
  position: number
  slot: number
  // members cut from each ranking group, by the group's key
  cut: Map<string, Set<string>>
}

const emptyAccumulator = (trackDistinct: boolean): PivotCellAccumulator => ({
  rows: 0,
  sum: 0,
  count: 0,
  distinct: trackDistinct ? new Set<string>() : undefined,
  numberCount: 0,
  max: undefined,
  min: undefined,
})

const accumulate = (cell: PivotCellAccumulator, value: unknown): void => {
  cell.rows += 1
  if (typeof value === 'number') {
    cell.sum += value
    cell.numberCount += 1
    if (cell.max === undefined || value > cell.max) cell.max = value
    if (cell.min === undefined || value < cell.min) cell.min = value
  }
  if (value !== null && value !== undefined && value !== '') {
    cell.count += 1
    cell.distinct?.add(String(value))
  }
}

/** Reads one aggregation out of a cell accumulator — no value when nothing contributed. */
const readAccumulator = (
  cell: PivotCellAccumulator | undefined,
  aggregation: PivotAggregationEnum
): number | undefined => {
  if (!cell || cell.rows === 0) return undefined
  if (aggregation === PivotAggregationEnum.Sum) return cell.sum
  if (aggregation === PivotAggregationEnum.Count) return cell.count
  if (aggregation === PivotAggregationEnum.DistinctCount) return cell.distinct?.size
  if (aggregation === PivotAggregationEnum.Average)
    return cell.numberCount > 0 ? cell.sum / cell.numberCount : undefined
  if (aggregation === PivotAggregationEnum.Max) return cell.max
  return cell.min
}

/** A single row's value for one formula reference — blanks count as missing under Count. */
const rowReferenceValue = (value: unknown, aggregation: PivotAggregationEnum): number => {
  if (aggregation === PivotAggregationEnum.Count || aggregation === PivotAggregationEnum.DistinctCount)
    return value === null || value === undefined || value === '' ? 0 : 1
  return typeof value === 'number' ? value : NaN
}

const rootNode = (): PivotNode => ({
  key: PIVOT_ROOT_KEY,
  member: { key: '', label: '', sortValue: '', isBlank: true },
  children: [],
  depth: 0,
})

/** Aggregates source rows into pivot trees and a measure lookup. */
export const buildPivot = ({
  data,
  layout,
  timeZone,
}: {
  data: PivotData
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>
  timeZone: string
}): PivotResult => {
  const fieldByName = new Map<string, PivotField>(data.fields.map((field) => [field.name, field]))
  const fieldIndex = new Map<string, number>(data.fields.map((field, index) => [field.name, index]))
  const rowFields = layout.rows.filter((item) => fieldByName.has(item.field))
  const columnFields = layout.columns.filter((item) => fieldByName.has(item.field))
  // a calculated measure joins only when its formula compiles and names response fields —
  // each formula compiles once per build, never per cell
  const compiled = layout.measures.flatMap((item): { measure: PivotLayoutMeasure; formula?: PivotFormula }[] => {
    if (isPivotCalculatedMeasure(item)) {
      let formula: PivotFormula
      try {
        formula = compilePivotFormula(item.formula)
      } catch {
        return []
      }
      return formula.references.every((reference) => fieldByName.has(reference.field))
        ? [{ measure: item, formula }]
        : []
    }
    return fieldByName.has(item.field) ? [{ measure: item }] : []
  })
  const measures = compiled.map((entry) => entry.measure)
  // one slot per kept measure in order, then one per distinct field + aggregation the
  // aggregated formulas reference; an individual formula carries its own evaluator
  const slots: PivotSlot[] = compiled.map((entry) => {
    if (!isPivotCalculatedMeasure(entry.measure))
      return { fieldIndex: fieldIndex.get(entry.measure.field) as number, aggregation: entry.measure.aggregation }
    if (entry.measure.individual === true && entry.formula !== undefined)
      return { aggregation: PivotAggregationEnum.Sum, formula: entry.formula }
    return { aggregation: PivotAggregationEnum.Sum }
  })
  const referenceSlot = new Map<string, number>()
  compiled.forEach((entry) => {
    if (isPivotCalculatedMeasure(entry.measure) && entry.measure.individual !== true && entry.formula !== undefined)
      entry.formula.references.forEach((reference) => {
        const key = `${reference.field}|${reference.aggregation}`
        if (!referenceSlot.has(key)) {
          referenceSlot.set(key, slots.length)
          slots.push({ fieldIndex: fieldIndex.get(reference.field) as number, aggregation: reference.aggregation })
        }
      })
  })
  const filters = layout.filters.filter((item) => fieldByName.has(item.field))

  const preparedFilters = filters.map((filter: PivotLayoutFilter) => ({
    index: fieldIndex.get(filter.field) as number,
    field: fieldByName.get(filter.field) as PivotField,
    dateGroup: filter.dateGroup,
    dateRange: filter.dateRange,
    matches: createPivotMemberMatcher(filter),
  }))

  const rowRoot = rootNode()
  const columnRoot = rootNode()
  const rowNodes = new Map<string, PivotNode>([[rowRoot.key, rowRoot]])
  const columnNodes = new Map<string, PivotNode>([[columnRoot.key, columnRoot]])
  // one accumulator array per (row node, column node) pair, indexed by slot
  const cells = new Map<PivotNode, Map<PivotNode, PivotCellAccumulator[]>>()
  const trackDistinct = slots.map((slot) => slot.aggregation === PivotAggregationEnum.DistinctCount)

  const memberKeyOf = (row: unknown[], item: PivotLayoutField): string =>
    toPivotMemberKey(
      row[fieldIndex.get(item.field) as number],
      fieldByName.get(item.field) as PivotField,
      item.dateGroup,
      timeZone
    )

  const extendPath = (
    row: unknown[],
    items: PivotLayoutField[],
    root: PivotNode,
    nodes: Map<string, PivotNode>
  ): PivotNode[] => {
    const path = [root]
    let parent = root
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const value = row[fieldIndex.get(item.field) as number]
      const pivotField = fieldByName.get(item.field) as PivotField
      const memberKey = memberKeyOf(row, item)
      // identical to JSON.stringify of the member keys from the root down, without re-encoding the prefix
      const key =
        parent.key === PIVOT_ROOT_KEY
          ? JSON.stringify([memberKey])
          : `${parent.key.slice(0, -1)},${JSON.stringify(memberKey)}]`
      let node = nodes.get(key)
      if (!node) {
        // the label is built once per distinct node, not once per row
        node = {
          key,
          member: toPivotMember(value, pivotField, item.dateGroup, timeZone),
          depth: parent.depth + 1,
          children: [],
        }
        nodes.set(key, node)
        parent.children.push(node)
      }
      path.push(node)
      parent = node
    }
    return path
  }

  const rowPasses = (row: unknown[]): boolean => {
    for (let i = 0; i < preparedFilters.length; i++) {
      const { index, field, dateGroup, dateRange, matches } = preparedFilters[i]
      const key = toPivotMemberKey(row[index], field, dateGroup, timeZone)
      if (!matches(key)) return false
      if (dateRange !== undefined && !isInPivotDateRange(key, dateRange)) return false
    }
    return true
  }

  // a field slot reads the row, an individual formula evaluates on it
  const readSlotValue = (slot: PivotSlot, row: unknown[]): unknown => {
    if (slot.formula !== undefined)
      return slot.formula.evaluate(
        slot.formula.references.map((reference) =>
          rowReferenceValue(row[fieldIndex.get(reference.field) as number], reference.aggregation)
        )
      )
    return slot.fieldIndex === undefined ? undefined : row[slot.fieldIndex]
  }

  // a ranking group holds the members compared with each other: the whole axis, or one parent's members
  const rankingKeys = ({ valueFilter, items, position }: PivotRankedFilter, row: unknown[]) => {
    const memberKeys = items.slice(0, position + 1).map((item) => memberKeyOf(row, item))
    const group =
      valueFilter.scope === PivotValueFilterScopeEnum.perParent ? JSON.stringify(memberKeys.slice(0, position)) : ''
    return { group, memberKey: memberKeys[position] }
  }

  // Top / Bottom N ranks over the rows every other filter lets through, so each cut is independent of the others
  const rankedFilters = filters.flatMap((filter): PivotRankedFilter[] => {
    const { valueFilter } = filter
    if (valueFilter === undefined || findPivotValueFilterProblem(layout, filter) !== undefined) return []
    const isSameItem = (item: PivotLayoutField) => item.field === filter.field && item.dateGroup === filter.dateGroup
    const items = rowFields.some(isSameItem) ? rowFields : columnFields
    const position = items.findIndex(isSameItem)
    const slot = measures.findIndex(
      (measure) =>
        !isPivotCalculatedMeasure(measure) &&
        measure.field === valueFilter.measure.field &&
        measure.aggregation === valueFilter.measure.aggregation
    )
    // a ranked field or measure the response lacks leaves the filter without effect
    if (position === -1 || slot === -1) return []
    if (valueFilter.scope === PivotValueFilterScopeEnum.perParent && position === 0) return []
    return [{ valueFilter, items, position, slot, cut: new Map() }]
  })

  if (!isEmpty(rankedFilters)) {
    const rankedRows = data.rows.filter(rowPasses)
    rankedFilters.forEach((ranked) => {
      const { valueFilter, items, position, slot } = ranked
      const item = items[position]
      const pivotField = fieldByName.get(item.field) as PivotField
      const groups = new Map<string, Map<string, { member: PivotMember; cell: PivotCellAccumulator }>>()
      rankedRows.forEach((row) => {
        const { group, memberKey } = rankingKeys(ranked, row)
        let members = groups.get(group)
        if (!members) {
          members = new Map()
          groups.set(group, members)
        }
        let entry = members.get(memberKey)
        if (!entry) {
          entry = {
            member: toPivotMember(row[fieldIndex.get(item.field) as number], pivotField, item.dateGroup, timeZone),
            cell: emptyAccumulator(slots[slot].aggregation === PivotAggregationEnum.DistinctCount),
          }
          members.set(memberKey, entry)
        }
        accumulate(entry.cell, readSlotValue(slots[slot], row))
      })
      groups.forEach((members, group) => {
        const ranking = [...members].map(([memberKey, entry]) => ({
          memberKey,
          member: entry.member,
          value: readAccumulator(entry.cell, slots[slot].aggregation),
        }))
        // member order first, so the stable value sort below leaves equal values in ascending member order
        const ordered = sortPivotMembers(ranking).sort((a, b) => {
          if (a.value === undefined || b.value === undefined)
            return Number(a.value === undefined) - Number(b.value === undefined)
          return valueFilter.direction === PivotValueFilterDirectionEnum.top ? b.value - a.value : a.value - b.value
        })
        const kept =
          valueFilter.mode === PivotValueFilterModeEnum.percent
            ? Math.max(1, Math.ceil((ordered.length * valueFilter.value) / 100))
            : valueFilter.value
        ranked.cut.set(group, new Set(ordered.slice(kept).map((entry) => entry.memberKey)))
      })
    })
  }

  const isCutByValueFilter = (row: unknown[]): boolean =>
    rankedFilters.some((ranked) => {
      const { group, memberKey } = rankingKeys(ranked, row)
      return ranked.cut.get(group)?.has(memberKey) === true
    })

  for (let n = 0; n < data.rows.length; n++) {
    const row = data.rows[n]
    if (!rowPasses(row) || isCutByValueFilter(row)) continue
    const slotValues = slots.map((slot) => readSlotValue(slot, row))
    const rowPath = extendPath(row, rowFields, rowRoot, rowNodes)
    const columnPath = extendPath(row, columnFields, columnRoot, columnNodes)
    for (let r = 0; r < rowPath.length; r++) {
      const rowNode = rowPath[r]
      let columnCells = cells.get(rowNode)
      if (!columnCells) {
        columnCells = new Map<PivotNode, PivotCellAccumulator[]>()
        cells.set(rowNode, columnCells)
      }
      for (let c = 0; c < columnPath.length; c++) {
        const columnNode = columnPath[c]
        let cell = columnCells.get(columnNode)
        if (!cell) {
          cell = slots.map((_, s) => emptyAccumulator(trackDistinct[s]))
          columnCells.set(columnNode, cell)
        }
        for (let s = 0; s < slots.length; s++) accumulate(cell[s], slotValues[s])
      }
    }
  }

  const sortTree = (node: PivotNode): void => {
    node.children = sortPivotMembers(node.children)
    node.children.forEach(sortTree)
  }
  sortTree(rowRoot)
  sortTree(columnRoot)

  const getValue = (rowKey: string, columnKey: string, measureIndex: number): number | undefined => {
    const measure = measures[measureIndex]
    const rowNode = rowNodes.get(rowKey)
    const columnNode = columnNodes.get(columnKey)
    const cell = rowNode && columnNode ? cells.get(rowNode)?.get(columnNode) : undefined
    if (!measure) return undefined
    if (!isPivotCalculatedMeasure(measure)) return readAccumulator(cell?.[measureIndex], measure.aggregation)
    if (measure.individual === true) {
      // per-row results summed during the pass — no value when no row gave a finite number
      const individual = cell?.[measureIndex]
      return individual !== undefined && individual.numberCount > 0 ? individual.sum : undefined
    }
    const formula = compiled[measureIndex]?.formula
    if (formula === undefined || cell === undefined) return undefined
    return formula.evaluate(
      formula.references.map((reference) =>
        readAccumulator(
          cell[referenceSlot.get(`${reference.field}|${reference.aggregation}`) as number],
          reference.aggregation
        )
      )
    )
  }

  return {
    rowFields,
    columnFields,
    measures,
    measureFormats: measures.map(resolvePivotMeasureFormat),
    rowRoot,
    columnRoot,
    getValue,
  }
}
